import { gConsts } from '@/globals/gConsts';
import Victor from 'victor';
import _ from 'lodash'
import profiler from '@/globals/Profiler';
import { mathUtils, Point } from '@/lib/math';
import { PolylineIndex, intersectPolylines, intersectSegments } from "@/lib/geom"
import { ToothRegionBuilder } from "./builders/ToothRegionBuilder"
import { ToothLongAxisBuilder } from './builders/ToothLongAxisBuilder';
import { LineSegmentRuler } from './LineSegmentRuler';


const ORDERED_TOOTH_NUMBERS_BY_POSITION_MAP = {
    'superior': {
        'esquerda': [18, 17, 16, 15, 14, 13, 12, 11],
        'direita': [21, 22, 23, 24, 25, 26, 27, 28],
    },
    'inferior': {
        'esquerda': [48, 47, 46, 45, 44, 43, 42, 41],
        'direita': [31, 32, 33, 34, 35, 36, 37, 38],
    },
}

export const MOLAR_TOOTH_NUMBERS = [
    18, 17, 16, 26, 27, 28,
    48, 47, 46, 36, 37, 38,
]

export const PERMANENTE_TOOTH_COUNT = 32;
export const ARCADA_TOOTH_COUNT = 16;

function findContourToolForTooth(tools, toothPosition) {
    return tools.find(tool => {
        if (tool.attrib.type != gConsts.tool.attrib.types.CURVA)
            return false;
        if (!gConsts.layer.locations.ANAT_CONTOURS.startsWith(tool.label.layer))
            return false;
        if (toothPosition == 'superior') {
            return tool.label.id == 'lb_cRebAlvSup';
        }
        return tool.label.id == 'lb_cRebAlvInf';
    });
}

export const toothUtils = {

    /**
     * Get the details of a tooth.
     * @param {number} numeroDente 
     * @returns {{numero: number, posicao: 'superior' | 'inferior', lado: 'esquerda' | 'direita', tipo: 'anterior' | 'posterior'}} The details of the tooth.
     */
    getToothDetails(numeroDente) {
        const posicao = numeroDente > 30 && numeroDente < 50 ? 'inferior' : 'superior';
        const lado = numeroDente >= 21 && numeroDente <= 38 ? 'direita' : 'esquerda';
        const tipo = [
            13, 12, 11, 21, 22, 23,
            43, 42, 41, 31, 32, 33,
        ].includes(numeroDente) ? 'anterior' : 'posterior';
        return { numero: numeroDente, posicao, lado, tipo };
    },

    isMolarTooth(numeroDente) {
        return MOLAR_TOOTH_NUMBERS.includes(+numeroDente);
    },

    /**
     * Get the most distant tooth on the arch.
     * @param {number[]} toothNumberList 
     * @param {'superior' | 'inferior'} posicao 
     * @param {'esquerda' | 'direita'} lado 
     * @returns {number} The most distant tooth on the arch.
     */
    getMostDistantToothNumberFromList(toothNumberList, posicao, lado) {
        const orderedToothNumbers = ORDERED_TOOTH_NUMBERS_BY_POSITION_MAP[posicao][lado];
        const filteredToothNumbers = orderedToothNumbers.filter(toothNumber => toothNumberList.includes(toothNumber));
        return Math.max(...filteredToothNumbers);
    },

    /**
     * Get the order index of a tooth.
     * @param {number} toothNumber 
     * @param {'complete' | 'side'} range 
     * @returns {number} The order index of the tooth.
     */
    getToothOrderIndex(toothNumber, range = 'complete') {
        const detalhesDente = toothUtils.getToothDetails(toothNumber);
        let orderedToothNumbers = [];
        let orderOffset = detalhesDente.posicao == 'superior' ? 0 : ARCADA_TOOTH_COUNT;
        if (range == 'complete') {
            orderedToothNumbers = [
                ...ORDERED_TOOTH_NUMBERS_BY_POSITION_MAP[detalhesDente.posicao]["esquerda"],
                ...ORDERED_TOOTH_NUMBERS_BY_POSITION_MAP[detalhesDente.posicao]["direita"]
            ];
        } else if (range == 'side') {
            orderedToothNumbers = ORDERED_TOOTH_NUMBERS_BY_POSITION_MAP[detalhesDente.posicao][detalhesDente.lado];
        }
        if (detalhesDente.posicao == 'inferior') {
            orderedToothNumbers = orderedToothNumbers.reverse();
        }
        return orderedToothNumbers.indexOf(toothNumber) + orderOffset;
    },

    /**
     * Get the order index of a tooth.
     * @param {number} toothNumber 
     * @param {'complete' | 'side'} range 
     * @returns {number} The order index of the tooth.
     */
    getToothHorizontalOrderIndex(toothNumber, range = 'complete', reverse = false) {
        const detalhesDente = toothUtils.getToothDetails(toothNumber);
        let orderedToothNumbers = [];
        if (range == 'complete') {
            orderedToothNumbers = [
                ...ORDERED_TOOTH_NUMBERS_BY_POSITION_MAP[detalhesDente.posicao]["esquerda"],
                ...ORDERED_TOOTH_NUMBERS_BY_POSITION_MAP[detalhesDente.posicao]["direita"]
            ];
        } else if (range == 'side') {
            orderedToothNumbers = ORDERED_TOOTH_NUMBERS_BY_POSITION_MAP[detalhesDente.posicao][detalhesDente.lado];
        }
        if (reverse) {
            orderedToothNumbers = orderedToothNumbers.reverse();
        }
        return orderedToothNumbers.indexOf(toothNumber);
    },

    getToothDetailsByLabelId(toothLabelId) {
        const numeroDente = parseInt(/lb_dente(\d+)/.exec(toothLabelId)[1]);
        return toothUtils.getToothDetails(numeroDente);
    },

    /**
     * 
     * @param {Object} toothTool 
     * @param {Object[]} tools 
     * @returns {[] | [Point, Point]}
     */
    getToothContourSegment(toothTool, tools) {
        if (toothTool.coords.contourSegment)
            return toothTool.coords.contourSegment;

        const detalhesDente = toothUtils.getToothDetailsByLabelId(toothTool.label.id);
        const contourTool = findContourToolForTooth(tools, detalhesDente.posicao);

        if (!contourTool)
            return [];

        const intersections = intersectPolylines(toothTool.coords.pts, contourTool.coords.pts, { closedA: true })
        if (intersections.length < 2)
            return [];

        const bestContourLine = LineSegmentRuler.getFarthestSegmentFromPointList(intersections.map(inter => inter.point))
        // console.debug(toothTool.label.id, "toothContour.bestContourLine", JSON.stringify(bestContourLine), '\n intersections:', intersections)

        // Ordeno ascendente pelo X (esquerda -> direita) para padronizar calculos.
        const contourSegment = bestContourLine.segment
            .sort((a, b) => a.x - b.x)

        toothTool.coords.contourSegment = contourSegment

        return toothTool.coords.contourSegment
    },

    /**
     * Get the points for the tooth region.
     * @param {Object} toothTool 
     * @param {Object[]} tools 
     * @param {'coroa' | 'raiz'} regionType 
     * @returns {Array} The points for the tooth region.
     */
    getToothRegionPts(toothTool, tools, regionType) {
        const toothContourSegment = toothUtils.getToothContourSegment(toothTool, tools);
        const detalhesDente = toothUtils.getToothDetailsByLabelId(toothTool.label.id);

        if (toothContourSegment.length < 2)
            return [];

        if (toothTool.coords.regionPts)
            return toothTool.coords.regionPts[regionType];
        const regionBuilder = new ToothRegionBuilder(
            toothTool.coords.pts,
            toothContourSegment
        )
        const regions = {
            coroa: regionBuilder.buildPoints(detalhesDente.posicao == 'inferior' ? 'left' : 'right'),
            raiz: regionBuilder.buildPoints(detalhesDente.posicao == 'inferior' ? 'right' : 'left')
        }
        // console.debug('ToothRegionBuilder regions', regions)
        toothTool.coords.regionPts = regions;
        return regions[regionType];
    },

    buildToothInternalLocations(toothTool, tools) {
        profiler.start('toothUtils');
        try {

            const internalLocationsPts = {
                faces: {
                    coroa__incisal: [],
                    coroa__oclusal: [],
                    coroa__mesial: [],
                    coroa__distal: [],
                    coroa__cervical: [],
                    raiz__distal: [],
                    raiz__mesial: [],
                },
                porcoes: {
                    raiz__cervical: [],
                    raiz__media: [],
                    raiz__apical: [],
                    dente_completo__coroa: [],
                    dente_completo__raiz: [],
                },
                others: {
                    longAxis: [],
                    longAxisBaseMandibula: [],
                }
            };
            // if (toothTool.label.id !== 'lb_dente46')
            //     return internalLocationsPts;

            internalLocationsPts.others.longAxis = toothUtils.getLongAxisLinePoints(toothTool, tools);

            const toothContourSegment = profiler.profileBlock('Method', 'getToothContourSegment', 'getToothContourSegment', () => toothUtils.getToothContourSegment(toothTool, tools));

            if (toothContourSegment.length < 2)
                return internalLocationsPts;

            const detalhesDente = toothUtils.getToothDetailsByLabelId(toothTool.label.id);

            if (detalhesDente.tipo == 'anterior') {
                internalLocationsPts.faces.coroa__incisal = toothUtils.buildFaceRegionPts(toothTool, tools, 'coroa', 'incisal');
            } else if (detalhesDente.tipo == 'posterior') {
                internalLocationsPts.faces.coroa__oclusal = toothUtils.buildFaceRegionPts(toothTool, tools, 'coroa', 'oclusal');
            }
            internalLocationsPts.faces.coroa__cervical = toothUtils.buildFaceRegionPts(toothTool, tools, 'coroa', 'cervical');
            internalLocationsPts.faces.coroa__mesial = toothUtils.buildFaceRegionPts(toothTool, tools, 'coroa', 'mesial');
            internalLocationsPts.faces.coroa__distal = toothUtils.buildFaceRegionPts(toothTool, tools, 'coroa', 'distal');
            internalLocationsPts.faces.raiz__distal = toothUtils.buildFaceRegionPts(toothTool, tools, 'raiz', 'distal');
            internalLocationsPts.faces.raiz__mesial = toothUtils.buildFaceRegionPts(toothTool, tools, 'raiz', 'mesial');

            internalLocationsPts.porcoes.dente_completo__coroa = toothUtils.buildPortionRegionPts(toothTool, tools, 'dente_completo', 'coroa');
            internalLocationsPts.porcoes.dente_completo__raiz = toothUtils.buildPortionRegionPts(toothTool, tools, 'dente_completo', 'raiz');
            internalLocationsPts.porcoes.raiz__cervical = toothUtils.buildPortionRegionPts(toothTool, tools, 'raiz', 'cervical');
            internalLocationsPts.porcoes.raiz__apical = toothUtils.buildPortionRegionPts(toothTool, tools, 'raiz', 'apical');
            internalLocationsPts.porcoes.raiz__media = toothUtils.buildPortionRegionPts(toothTool, tools, 'raiz', 'media');

            // internalLocationsPts.others.longAxis = toothUtils.getLongAxisLinePoints(toothTool, tools);
            internalLocationsPts.others.longAxisBaseMandibula = toothUtils.buildLongAxisBaseMandibulaLinePoints(toothTool, tools);
            // internalLocationsPts.faces.contour = contourPts.map(pt => pt.refToothPt);

            return internalLocationsPts
        } finally {
            profiler.end();
        }
    },

    /**
     * 
     * @param {Object} toothTool 
     * @param {[{x: number, y:number}, {x: number, y:number}]} longAxisLineSegment 
     * @param {'dente_completo' | 'raiz'} portionType 
     * @param {'apical' | 'media' | 'cervical' | 'coroa' | 'raiz'} regionType 
     * @returns 
     */
    buildPortionRegionPts(toothTool, tools, portionType, regionType) {
        if (portionType == 'dente_completo') {
            const toothRegionPts = toothUtils.getToothRegionPts(toothTool, tools, regionType);
            return _.cloneDeep(toothRegionPts);
        }
        const toothContourSegment = toothUtils.getToothContourSegment(toothTool, tools);
        const detalhesDente = toothUtils.getToothDetailsByLabelId(toothTool.label.id);
        const toothRegionPts = toothUtils.getToothRegionPts(toothTool, tools, 'raiz');
        const longAxisRegionSegment = toothUtils.buildLongAxisRegionSegmentPts(toothTool, tools, 'raiz');

        var regionPts = _.cloneDeep(toothRegionPts);

        if (['apical', 'media', 'cervical'].includes(regionType)) {
            if (longAxisRegionSegment.length == 0 || toothContourSegment.length == 0)
                return [];

            const edgeLongAxisDistance = Victor.fromObject(longAxisRegionSegment[1])
                .subtract(Victor.fromObject(longAxisRegionSegment[0]))
                .length();
            // Apical: from root to segment
            // Cervical: from contour to segment
            // Media: between apical and cervical segments
            const coroaSide = detalhesDente.posicao == 'inferior' ? 'left' : 'right'
            const raizSide = detalhesDente.posicao == 'inferior' ? 'right' : 'left'
            const parallelDistanceMultiplerByRegion = {
                'cervical': 0.25,
                'apical': 0.75
            }
            // return contourParallelSegment

            if (regionType == 'media') {
                const contourParallelSegmentA = mathUtils.getParallelLineSegment(
                    toothContourSegment,
                    edgeLongAxisDistance * parallelDistanceMultiplerByRegion['cervical'],
                    raizSide
                );
                const contourParallelSegmentB = mathUtils.getParallelLineSegment(
                    toothContourSegment,
                    edgeLongAxisDistance * parallelDistanceMultiplerByRegion['apical'],
                    raizSide
                );
                const regionAPoints = new ToothRegionBuilder(regionPts, contourParallelSegmentA)
                    .buildPoints(raizSide)

                return new ToothRegionBuilder(regionAPoints, contourParallelSegmentB)
                    .buildPoints(coroaSide);
            } else {
                const contourParallelSegment = mathUtils.getParallelLineSegment(
                    toothContourSegment,
                    edgeLongAxisDistance * parallelDistanceMultiplerByRegion[regionType],
                    raizSide
                );

                const regionBuilder = new ToothRegionBuilder(regionPts, contourParallelSegment);
                return regionBuilder.buildPoints(regionType == 'cervical' ? coroaSide : raizSide);
            }
        }

        return [];
    },

    /**
     * 
     * @param {Object} toothTool 
     * @param {[{x: number, y:number}, {x: number, y:number}]} longAxisLineSegment 
     * @param {'coroa' | 'raiz'} faceType 
     * @param {'mesial' | 'distal' | 'incisal' | 'oclusal' | 'cervical'} regionType 
     * @returns 
     */
    buildFaceRegionPts(toothTool, tools, faceType, regionType) {
        const toothRegionPts = toothUtils.getToothRegionPts(toothTool, tools, faceType);
        const toothContourSegment = toothUtils.getToothContourSegment(toothTool, tools);
        const longAxisRegionSegment = toothUtils.buildLongAxisRegionSegmentPts(toothTool, tools, faceType);
        const detalhesDente = toothUtils.getToothDetailsByLabelId(toothTool.label.id);

        var regionPts = _.cloneDeep(toothRegionPts),
            longAxisSubLine = [],
            longAxisToEdgePerpendicularLine,
            regionLongAxisPerpendicularIntersections;

        // console.log(toothTool.label.id, 'buildFaceRegionPts.longAxisLineSegment', longAxisRegionSegment)

        if (longAxisRegionSegment.length == 0 || toothContourSegment.length == 0)
            return [];

        const regionPolyline = new PolylineIndex(regionPts, { closed: true })

        if (['mesial', 'distal'].includes(regionType)) {

            // Lado relacionado ao segmento longo eixo (coroa -> raiz)
            let longAxisSide = (detalhesDente.lado == 'esquerda' && regionType == 'mesial') || (detalhesDente.lado == 'direita' && regionType == 'distal') ? 'left' : 'right';
            // Invertida para superior porque o longo eixo fica apontado para cima (coroa -> raiz)
            if (detalhesDente.posicao == 'superior')
                longAxisSide = longAxisSide == 'left' ? 'right' : 'left'

            const longAxisSublineMultipler = faceType == 'raiz' ? 0.3 : 0.5

            longAxisSubLine = mathUtils.extendLineSegment(longAxisRegionSegment, longAxisSublineMultipler);

            // console.debug('regionType -> longAxisSide:', regionType, longAxisSide)
            longAxisToEdgePerpendicularLine = mathUtils.getPerpendicularLineSegment(longAxisSubLine, longAxisSide);
            // return longAxisToEdgePerpendicularLine;
            longAxisToEdgePerpendicularLine = mathUtils.extendLineSegment(longAxisToEdgePerpendicularLine, 5)

            // return longAxisToEdgePerpendicularLine;
            // // Get a line from the middle point of the longAxis until the extreme of the tooth related to the side it belongs to.
            regionLongAxisPerpendicularIntersections = regionPolyline.intersectSegment(longAxisToEdgePerpendicularLine[0], longAxisToEdgePerpendicularLine[1])
            const edgeIntersectionPoint = regionLongAxisPerpendicularIntersections.at(0)?.point
            // console.debug('buildFaceRegionPts.regionLongAxisPerpendicularIntersections', regionLongAxisPerpendicularIntersections)
            if (!edgeIntersectionPoint)
                return []

            var edgeToLongAxisPerpendicularLine = [
                edgeIntersectionPoint,
                longAxisToEdgePerpendicularLine[0]
            ]

            // edgeToLongAxisPerpendicularLine = mathUtils.extendLineSegment(edgeToLongAxisPerpendicularLine, 0.5)
            let edgeToLongAxisPerpendicularDistance = Victor.fromObject(edgeToLongAxisPerpendicularLine[1])
                .subtract(Victor.fromObject(edgeToLongAxisPerpendicularLine[0]))
                .length();

            const averageEdgesDistance = Victor.fromObject(toothContourSegment[1])
                .subtract(Victor.fromObject(toothContourSegment[0]))
                .length() / 2;

            let longAxisDistanceMultipler = detalhesDente.tipo == 'posterior' ? 0.4 : 0.25
            const longAxisAverageEdgeDifference = faceType == 'raiz' ?
                (edgeToLongAxisPerpendicularDistance - averageEdgesDistance) : 0
            const longAxisParallelSegment = mathUtils.getParallelLineSegment(
                longAxisRegionSegment,
                Math.max(averageEdgesDistance * 0.1, ((averageEdgesDistance * longAxisDistanceMultipler) + longAxisAverageEdgeDifference)),
                longAxisSide
            );
            // return longAxisParallelSegment;
            // console.debug(toothTool.label.id, 'longAxisParallelSegment', longAxisParallelSegment)
            const regionBuilder = new ToothRegionBuilder(regionPts, longAxisParallelSegment);
            return regionBuilder.buildPoints(longAxisSide);
        } else if (['cervical', 'oclusal', 'incisal'].includes(regionType) && faceType == 'coroa') {

            const edgeLongAxisDistance = Victor.fromObject(longAxisRegionSegment[1])
                .subtract(Victor.fromObject(longAxisRegionSegment[0]))
                .length();

            const coroaSide = detalhesDente.posicao == 'inferior' ? 'left' : 'right'
            const raizSide = detalhesDente.posicao == 'inferior' ? 'right' : 'left'
            const parallelDistanceMultipler = ['cervical'].includes(regionType) ? 0.25 : 0.7
            const contourParallelSegment = mathUtils.getParallelLineSegment(
                toothContourSegment,
                edgeLongAxisDistance * parallelDistanceMultipler,
                coroaSide
            );
            // return contourParallelSegment

            const regionBuilder = new ToothRegionBuilder(regionPts, contourParallelSegment);
            return regionBuilder.buildPoints(['cervical'].includes(regionType) ? raizSide : coroaSide);
        }

        return [];
    },

    /**
     * Build the points for the tooth long axis.
     * @param {Object} toothTool 
     * @param {Object[]} tools 
     * @param {'coroa' | 'raiz'} regionType 
     * @returns {Array} The points for the tooth long axis.
     */
    buildLongAxisRegionSegmentPts(toothTool, tools, regionType) {
        if (toothTool.coords.longAxisSegment?.[regionType])
            return toothTool.coords.longAxisSegment[regionType];

        const regionPts = toothUtils.getToothRegionPts(toothTool, tools, regionType);
        const longAxisLinePoints = toothUtils.getLongAxisLinePoints(toothTool, tools);
        // console.log('buildLongAxisRegionSegmentPts.longAxisLinePoints', longAxisLinePoints)
        if (!longAxisLinePoints.length)
            return [];

        // Considerando longo eixo sempre na direcao (coroa -> raiz)
        const longAxisPontoCoroa = longAxisLinePoints[0]
        const longAxisPontoRaiz = longAxisLinePoints[1]

        const toothContourSegment = toothUtils.getToothContourSegment(toothTool, tools);
        if (toothContourSegment.length < 2)
            return [];

        const contourLongAxisIntersectionPoint = intersectSegments(toothContourSegment[0], toothContourSegment[1], longAxisPontoCoroa, longAxisPontoRaiz)
        if (!contourLongAxisIntersectionPoint)
            return [];

        var longAxisContourToToothEdge = regionType == 'coroa' ?
            [contourLongAxisIntersectionPoint, longAxisPontoCoroa] :
            [contourLongAxisIntersectionPoint, longAxisPontoRaiz];

        // Mantenho a distancia atual para raiz para nao dar problema para os dentes com 2 raizes
        if (regionType == 'raiz')
            return [contourLongAxisIntersectionPoint, longAxisPontoRaiz];

        // Extendo um pouco para garantir que bate intersecao de segmento (finito)
        longAxisContourToToothEdge = mathUtils.extendLineSegment(longAxisContourToToothEdge, 0.9, 'both');
        longAxisContourToToothEdge = mathUtils.extendLineSegment(longAxisContourToToothEdge, 5, 'end');
        const regionPolyline = new PolylineIndex(regionPts, { closed: true })
        const regionLongAxisIntersections = regionPolyline.intersectSegment(longAxisContourToToothEdge[0], longAxisContourToToothEdge[1])

        const bestRegionLongAxisIntersection = regionLongAxisIntersections
            // .sort((a, b) => (a.tB - 0.5) - (b.tB - 0.5))
            .at(0)

        if (!bestRegionLongAxisIntersection)
            return [];

        if (toothTool.label.id == 'lb_dente45') {
            // console.debug(toothTool.label.id, regionType, 'bestRegionLongAxisIntersection: ', JSON.stringify(bestRegionLongAxisIntersection));
        }
        var longAxisRegionLine = []
        // Garanto que sempre vai estar na direcao (coroa -> raiz)
        if (regionType == 'coroa')
            longAxisRegionLine = [bestRegionLongAxisIntersection.point, contourLongAxisIntersectionPoint]
        else
            longAxisRegionLine = [contourLongAxisIntersectionPoint, bestRegionLongAxisIntersection.point]

        if (!toothTool.coords.longAxisSegment)
            toothTool.coords.longAxisSegment = {};
        toothTool.coords.longAxisSegment[regionType] = longAxisRegionLine;

        return longAxisRegionLine;
    },

    /**
     * Retorna os pontos da linha do longo eixo do dente.
     * 
     * Direcao: coroa -> raiz.
     * @param {Object} toothTool 
     * @param {Object[]} tools 
     * @returns {[Point, Point]}
     */
    getLongAxisLinePoints(toothTool, tools) {
        if (!toothTool)
            return [];
            
        if (toothTool.coords.longAxis?.length > 0)
            return toothTool.coords.longAxis

        // const longAxisTool = tools
        //     .filter(tool => tool.label.layer == gConsts.layer.locations.TEETH_LE)
        //     .find(tool => tool.label.id == toothTool.label.id);

        // Desativado uso do tool longo eixo.
        // A precisao que vem é menor do que fazer manualmente.
        // if (longAxisTool) {
        //     // console.log('getLongAxisLinePoints.longAxisTool')

        //     const validationResult = toothUtils.validateLongAxisTool(longAxisTool, toothTool, tools)
        //     console.log(toothTool.label.id, 'getLongAxisLinePoints.longAxisTool.validationResult', validationResult)

        //     toothTool.coords.longAxis = longAxisTool.coords.pts
        //     return longAxisTool.coords.pts
        // }

        // console.debug('building longAxis', toothTool.label.id)

        const detalhesDente = toothUtils.getToothDetailsByLabelId(toothTool.label.id)
        let longAxisPts = new ToothLongAxisBuilder(toothTool.coords.pts, detalhesDente.posicao, toothTool.label.id)
            .build()

        // // Uso tool do longo eixo como fallback
        // if (longAxisPts.length != 2 && longAxisTool) {
        //     longAxisPts = longAxisTool.coords.pts
        // }

        toothTool.coords.longAxis = longAxisPts
        return longAxisPts
    },

    validateLongAxisTool(longAxisTool, toothTool, tools) {
        const toothContourSegment = toothUtils.getToothContourSegment(toothTool, tools);
        const averageEdgesDistance = Victor.fromObject(toothContourSegment[1])
            .subtract(Victor.fromObject(toothContourSegment[0]))
            .length() / 2;

        const longAxisSegment = [
            longAxisTool.coords.pts[0],
            longAxisTool.coords.pts[1],
        ]

        const longAxisMiddlePoint = mathUtils.getMidPoint(longAxisSegment[0], longAxisSegment[1])
        const distanceFromContourSegmentA = Victor.fromObject(toothContourSegment[0])
            .subtract(Victor.fromObject(longAxisMiddlePoint))
            .length();
        const distanceFromContourSegmentB = Victor.fromObject(toothContourSegment[1])
            .subtract(Victor.fromObject(longAxisMiddlePoint))
            .length();
        
        const averageLongAxisDistance = (distanceFromContourSegmentA + distanceFromContourSegmentB) / 2

        const rate = Math.min(averageLongAxisDistance, averageEdgesDistance) / Math.max(averageLongAxisDistance, averageEdgesDistance)

        return {
            rate: rate,
            valid: rate > 0.8
        }
    },

    buildLongAxisBaseMandibulaLinePoints(toothTool, tools) {
        if (toothTool.coords.longAxisBaseMandibula?.length > 0)
            return toothTool.coords.longAxisBaseMandibula;

        const toothDetails = toothUtils.getToothDetailsByLabelId(toothTool.label.id);
        if (toothDetails.posicao != 'inferior')
            return [];

        const contornoMandibulaTool = tools
            .find(tool => tool.label.id == 'lb_cContMand' && tool.attrib.type == gConsts.tool.attrib.types.CURVA)

        if (!contornoMandibulaTool)
            return [];

        const longAxisLinePoints = toothUtils.getLongAxisLinePoints(toothTool, tools);
        if (longAxisLinePoints.length != 2)
            return [];
        const contornoPolyline = new PolylineIndex(contornoMandibulaTool.coords.pts, { closed: true })

        let longAxisExtended = _.clone(longAxisLinePoints);
        longAxisExtended = mathUtils.extendLineSegment(longAxisExtended, 5, 'end');
        const mandibulaIntersections = contornoPolyline.intersectSegment(longAxisExtended[0], longAxisExtended[1])
        if (!mandibulaIntersections.length)
            return []

        // Ordeno pela maior numero trajetoria que indica que está mais na direcao correta da mandibula, 
        // pois longo eixo pode bater tambem no lado contrario junto com rebordo.
        const bestMandibulaIntersectionPoint = mandibulaIntersections
            .sort((a, b) => b.tA - a.tA)
            .at(0)
            .point

        // console.log(toothTool.label.id, 'mandibulaIntersections', mandibulaIntersections)

        const longAxisEndToMandibulaSegment = [
            longAxisLinePoints[1],
            bestMandibulaIntersectionPoint
        ]

        toothTool.coords.longAxisBaseMandibula = longAxisEndToMandibulaSegment

        return longAxisEndToMandibulaSegment;
    },

}